<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            ref="grid"
            :defaultColDef="defaultColDef"
            :autoGroupColumnDef="autoGroupColumnDef"
            :columnDefs="columnDefs"
            :treeData="true"
            :isSearch="false"
            :getDataPath="getDataPath"
            :rowData="rowData"
            :pinnedBottomRowData="pinnedBottomRowData"
            :getRowClass="getRowClass"
            :getRowStyle="getRowStyle"
            :isAutoWidth="true"
            grid-theme="ag-theme-alpine-report"
            :isGroupOpenByDefault="() => expanded"
        >
            <date-range-picker
                :range-options="dateRangePickerOptions"
                :value="datesValue"
                :date-format="userUnicodeDateFormat"
                @input="onDateRangePickerInput"
            ></date-range-picker>
            <responsive-dropdown :icon="['fas', 'filter']" :items="statusOptions" variant="primary">{{
                selectedStatusLabel
            }}</responsive-dropdown>
            <responsive-dropdown :icon="['fas', 'dollar-sign']" :items="filterOptions" variant="primary">{{
                selectedFilterLabel
            }}</responsive-dropdown>
            <responsive-dropdown :icon="['fas', 'users']" :items="subscriberStatusOptions" variant="primary">{{
                selectedSubscriptionStatusLabel
            }}</responsive-dropdown>

            <responsive-button
                :icon="['fas', 'up-right-and-down-left-from-center']"
                variant="primary"
                @click="onButtonExpand"
            >
                <span v-if="expanded">Collapse</span>
                <span v-else>Expand</span>
            </responsive-button>
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../../api';
import Page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    THIS_YEAR,
    LAST_YEAR,
} from '../../generic/date-range-picker/date-range-picker.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
import { SERVER_DATE_FORMAT } from '../../../constants';
import { ADMIN_GUEST_VERIFICATIONS_BILLING_REPORT_ENDPOINT } from '../../../endpoints';

const autoGroupValueFormatter = ({ node, data }) => {
    return data.subscriber_id && node.level === 0
        ? `(${data.subscriber_id}, ${data.subscriber_status}) ${data.name}`
        : '';
};
export default {
    components: {
        Grid,
        Page,
        DateRangePicker,
        ResponsiveDropdown,
        ResponsiveButton,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            autoGroupColumnDef: {
                flex: 2,
                minWidth: 200,
                resizable: true,
                sortable: true,
                sort: 'asc',
                comparator: (valueA, valueB, nodeA, nodeB) => {
                    return nodeA.data.name > nodeB.data.name ? 1 : -1;
                },
                field: 'name',
                valueFormatter: autoGroupValueFormatter,
                headerName: 'Company',
                cellStyle: { textAlign: 'center' },
            },
            busy: false,
            defaultColDef: {
                minWidth: 80,
                sortable: false,
            },
            getDataPath: (data) => data.id_hierarchy,
            gridContainerEl: null,
            gridApi: null,
            gridColumnApi: null,
            expanded: true,
            datesValue: nameToRange(LAST_MONTH),
            dateRangePickerOptions: [TODAY, YESTERDAY, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR],
            rowData: [],
            selectedStatus: null,
            selectedStatusLabel: 'All Attempts',
            selectedSubscriptionStatus: null,
            selectedSubscriptionStatusLabel: 'All Subscriptions',
            selectedFilterLabel: 'All',
            selectedFilter: 'ALL',
            tabs: [
                {
                    title: 'Guest Verification Billing',
                    active: true,
                },
            ],
            totalPaid: null,
            totalPending: null,
        };
    },
    mounted() {
        const grid = this.$refs.grid;
        this.gridContainerEl = grid.$refs.gridContainer;
        this.gridApi = grid.gridOptions.api;
        this.gridColumnApi = grid.gridOptions.columnApi;
        (async () => {
            await this.fetchGridRowData();
        })();
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        pinnedBottomRowData() {
            return [
                {
                    total_amount_pending: this.totalPending,
                    total_amount_paid: this.totalPaid,
                },
            ];
        },
        statusOptions() {
            const statuses = [
                {
                    value: null,
                    text: 'All Attempts',
                },
                {
                    value: 'FAILURE',
                    text: 'Failure Attempts',
                },
                {
                    value: 'SUCCESS',
                    text: 'Success Attempts',
                },
            ];
            return statuses.map((item) => ({
                label: item.text,
                click: () => {
                    this.selectedStatusLabel = item.text;
                    this.selectedStatus = item.value;
                    this.refresh();
                },
            }));
        },
        subscriberStatusOptions() {
            const statuses = [
                {
                    value: null,
                    text: 'All Subscriptions',
                },
                {
                    value: 'TRIAL',
                    text: 'Trial Subscriptions',
                },
                {
                    value: 'ACTIVE',
                    text: 'Active Subscriptions',
                },
                {
                    value: 'CANCELLED',
                    text: 'Cancelled Subscriptions',
                },
                {
                    value: 'TRIALSUSPEND',
                    text: 'Trial Suspend Subscriptions',
                },
                {
                    value: 'SUSPENDED',
                    text: 'Suspended Subscriptions',
                },
                {
                    value: 'CREATED',
                    text: 'Created Subscriptions',
                },
                {
                    value: 'GRACE',
                    text: 'Grace Perios Subscriptions',
                },
                {
                    value: 'PENDING',
                    text: 'Pending Subscriptions',
                },
            ];
            return statuses.map((item) => ({
                label: item.text,
                click: () => {
                    this.selectedSubscriptionStatusLabel = item.text;
                    this.selectedSubscriptionStatus = item.value;
                    this.refresh();
                },
            }));
        },
        filterOptions() {
            const filters = [
                {
                    value: 'ALL',
                    text: 'All',
                },
                {
                    value: 'PENDING',
                    text: 'Uncollected Only',
                },
                {
                    value: 'PAID',
                    text: 'Paid Only',
                },
            ];
            return filters.map((item) => ({
                label: item.text,
                click: () => {
                    this.selectedFilterLabel = item.text;
                    this.selectedFilter = item.value;
                    this.refresh();
                },
            }));
        },
        columnDefs() {
            return [
                {
                    field: 'name',
                    headerName: 'Company',
                    hide: true,
                },
                {
                    field: 'guest_email',
                    headerName: 'Guest Email',
                    sortable: true,
                    width: 100,
                    minWidth: 100,
                },

                {
                    field: 'created',
                    headerName: 'Created',
                    type: 'dateTimeColumn',
                    sortable: true,
                    minWidth: 130,
                },
                {
                    field: 'guest_name',
                    headerName: 'Guest Name',
                    sortable: true,
                    width: 110,
                    minWidth: 110,
                },
                {
                    field: 'status',
                    headerName: 'Attempt Status',
                    width: 100,
                    minWidth: 100,
                    cellRenderer: ({ data, node }) =>
                        !node.rowPinned && data.created
                            ? `<span class="${data.status === 'SUCCESS' ? 'text-success' : 'text-danger'}">${
                                  data.status
                              }</span>`
                            : '',
                },
                {
                    field: 'transact_code',
                    headerName: 'Transact. Code',
                    width: 100,
                    minWidth: 100,
                },
                {
                    field: 'collect_status',
                    headerName: 'Collect Status',
                    width: 100,
                    minWidth: 100,
                    sortable: true,
                },
                {
                    field: 'collect_date',
                    headerName: 'Collect Date',
                    type: 'dateColumn',
                    width: 100,
                    minWidth: 100,
                },
                {
                    field: 'invoice_no',
                    headerName: 'Invoice #',
                    width: 110,
                    minWidth: 110,
                },
                {
                    field: 'total_amount_pending',
                    headerName: 'Total $ Pending',
                    type: 'amountColumn',
                    sortable: false,
                    hide: this.selectedFilter === 'PAID',
                },
                {
                    field: 'total_amount_paid',
                    headerName: 'Total $ Paid',
                    type: 'amountColumn',
                    sortable: false,
                    hide: this.selectedFilter === 'PENDING',
                },
            ];
        },
    },
    methods: {
        async onDateRangePickerInput(dates) {
            this.datesValue = { ...dates };
            await this.fetchGridRowData();
        },
        async fetchGridRowData() {
            this.busy = true;
            try {
                const response = await API.get(ADMIN_GUEST_VERIFICATIONS_BILLING_REPORT_ENDPOINT(), {
                    params: {
                        from_date: format(this.datesValue.from, SERVER_DATE_FORMAT),
                        to_date: format(this.datesValue.to, SERVER_DATE_FORMAT),
                        status: this.selectedStatus,
                        filter_by: this.selectedFilter,
                        subscription_status: this.selectedSubscriptionStatus,
                    },
                });
                this.rowData = response.data.items;
                this.totalPending = response.data.total_pending;
                this.totalPaid = response.data.total_paid;
            } catch (err) {
                this.errorToast({ text: 'Could not load report data.' });
            } finally {
                this.busy = false;
                if (this.expanded) {
                    setTimeout(() => this.expandLevel0());
                }
            }
        },
        refresh() {
            this.fetchGridRowData();
        },
        expandLevel0() {
            this.gridApi.collapseAll();
            this.gridApi.forEachNode((node) => {
                if (node.level === 0) {
                    node.setExpanded(true);
                }
            });
            this.expanded = true;
        },
        onButtonExpand() {
            if (this.expanded) {
                this.gridApi.collapseAll();
                this.expanded = false;
            } else {
                this.expandLevel0();
            }
        },
        getRowClass({ data }) {
            if (!data.created) {
                return 'lx-row-group';
            }
            return '';
        },
        getRowStyle({ data, node }) {
            if (!data.created) {
                node.pdfRowStyle = {
                    bold: true,
                };
                if (node.rowIndex !== 0) {
                    node.pdfRowStyle.margin = [0, 15, 0, 0];
                }
            }
        },
    },
};
</script>

<style>
</style>